import {
  FluidAction,
  FluidEmptyStateConfig,
  FluidStatefulTableConfig,
  FluidTableAction,
  FluidTableFormActionsConfig,
} from '@lmig/fluid-core';
import { FluidTableFormButtonConfig } from '@lmig/fluid-core/lib/structs/interfaces/fluid-table-form-button-config.interface';

import { Utils } from '../../../utils/utils';
import { TableControlsPlacement } from '../components/fluid-table-controls.components';
import { FluidTableEmptyStateConfig } from '../components/fluid-table-empty-state.components';
import { defaultStatefulOptions } from '../features/table-defaults.config';

export type TableFilterRef =
  | HTMLFluidComboBoxElement
  | HTMLFluidContextMenuElement;

export function upgradeFormActions(
  tableId: string,
  showEditActions: boolean,
  editActionsPlacement: TableControlsPlacement,
  formActionsConfig: FluidTableFormActionsConfig
) {
  return {
    showEditActions,
    editActionsPlacement,
    submitButtonConfig: upgradeFormActionConfig(
      formActionsConfig?.submitButtonConfig,
      'submitButton',
      tableId
    ),
    cancelButtonConfig: upgradeFormActionConfig(
      formActionsConfig?.cancelButtonConfig,
      'cancelButton',
      tableId
    ),
    ...formActionsConfig,
  };
}

function upgradeFormActionConfig(
  deprecatedConfig: FluidTableFormButtonConfig,
  identifierSuffix: string,
  tableId: string
) {
  return {
    ...deprecatedConfig,
    identifier:
      deprecatedConfig?.identifier || `${tableId}_${identifierSuffix}`,
  };
}

/**
 * If emptyStateConfig is the now deprecated FluidTableEmptyStateConfig, map its
 * properties to the newer FluidEmptyStateConfig interface
 * @param emptyStateConfig
 */
export function upgradeEmptyStateConfig(
  emptyStateConfig: FluidTableEmptyStateConfig | FluidEmptyStateConfig
): FluidEmptyStateConfig {
  if (!emptyStateConfig) return;
  if (isTableEmptyStateConfig(emptyStateConfig)) {
    Utils.deprecationWarning(
      { tagName: 'fluid-table' } as HTMLElement,
      'Prop',
      'emptyStateConfig',
      'emptyStateConfig',
      'FluidEmptyStateConfig',
      'FluidTableEmptyStateConfig'
    );
    return {
      titleText: (emptyStateConfig as FluidTableEmptyStateConfig).message,
      iconConfig: (emptyStateConfig as FluidTableEmptyStateConfig).icon,
      action: mapTableActionsToActions(
        (emptyStateConfig as FluidTableEmptyStateConfig).actions ||
          (emptyStateConfig as FluidTableEmptyStateConfig).action
      ),
    } as FluidEmptyStateConfig;
  }
  return emptyStateConfig as FluidEmptyStateConfig;
}

export function upgradeStatefulOptions(
  statefulOptions: FluidStatefulTableConfig,
  stateful?: boolean
) {
  if (stateful) {
    Utils.deprecationWarning(
      { tagName: 'fluid-table' } as HTMLElement,
      'prop',
      'stateful',
      'statefulOptions',
      'FluidStatefulTableConfig',
      'boolean'
    );
    return defaultStatefulOptions(stateful);
  }
  return statefulOptions;
}

/**
 * Maps an array of table actions to plain `FluidAction`
 * @param actions
 */
function mapTableActionsToActions(
  actions: FluidTableAction | FluidTableAction[]
): FluidAction[] {
  const tableActionToAction = (action: FluidTableAction): FluidAction => ({
    ...action.buttonConfig,
    identifier: action.actionKey,
  });
  if (!actions || (actions as FluidTableAction[])?.length === 0) return [];
  if (!Array.isArray(actions)) {
    return [tableActionToAction(actions)];
  }
  return actions.map((action: FluidTableAction) => tableActionToAction(action));
}

/**
 * Checks if the emptyStateConfig is an instance of FluidTableEmptyStateConfig
 * by querying the existence of a `message` member.
 * @param emptyStateConfig
 */
function isTableEmptyStateConfig(
  emptyStateConfig: FluidTableEmptyStateConfig | FluidEmptyStateConfig
): boolean {
  return !!(emptyStateConfig as FluidTableEmptyStateConfig).message;
}
